/**
 * Returns sum of a specified property in an array of objects.
 * 
 * @param array The array to sum values from.
 * @param fieldFn Selector for which property to sum. Example: "(item) => item.cost"
 */
export const sumBy = <T>(array: T[], fieldFn: (item: T) => number = item => (item as any) as number): number => {
    return array.reduce((a, b) => a + fieldFn(b), 0);
};

export type Grouped<TData, TKey> = {
    key: TKey;
    items: TData[];
};

/**
 * Group an array by a specified property. For example, group an object array in to year buckets.
 * 
 * @param arr The array to process into groups.
 * @param fieldFn Selector for which property to sum. Example: "(item) => item.year".
 */
export const groupBy = <TData, TKey>(arr: TData[], fieldFn: (item: TData) => TKey) => {
    return arr.reduce((acc, currItem) => {
        const key = fieldFn(currItem);
        let item = acc.find(group => group.key === key);

        if (item) {
            item.items.push(currItem);
        } else {
            return [...acc, { key: key, items: [currItem] }];
        }

        return acc;
    }, [] as Grouped<TData, TKey>[]);
};

/**
 * Sorts an array by a specified number property. Returns a new array with sorted data.
 * 
 * @param arr The array to sort.
 * @param fieldFn Selector for which property to sum. Example: "(item) => item.year.
 */
export const sortBy = <T>(arr: T[], fieldFn: (item: T) => number) => {
    return arr.sort((a, b) => {
        const aVal = fieldFn(a);
        const bVal = fieldFn(b);

        return aVal > bVal ? 1 : bVal > aVal ? -1 : 0;
    });
};
